import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for AboutPage
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "プロジェクトについて",
      "description": "琉球人オーナーのお店を応援する、コミュニティ主導のマップサービスです。",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "まーやいびーが？ Ryukyu ゆいまーる MAP",
        "description": "琉球の文化と伝統を大切にしながら、現代に生きる琉球人オーナーたちが営むお店を、より多くの人に知ってもらうためのプロジェクトです。",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF8F3] via-[#FFFBF7] to-[#FFF5ED]">
      <Header />

      <div className="pt-[120px] pb-24 px-6">
        <div className="max-w-[900px] mx-auto">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-16 text-center"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#FFE8DC] to-[#FFF0E6] mb-5">
              <span className="text-[13px] font-bold bg-gradient-to-r from-[#FF9E7A] to-[#FFBC9E] bg-clip-text text-transparent tracking-[1.5px] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                ✦ ABOUT US
              </span>
            </div>
            <h1 className="text-[48px] font-bold bg-gradient-to-r from-[#19425B] to-[#FF9E7A] bg-clip-text text-transparent leading-[1.3] mb-5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              プロジェクトについて
            </h1>
            <p className="text-[18px] text-[rgba(25,66,91,0.65)] leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              琉球人オーナーのお店を応援する、<br />
              コミュニティ主導のマップサービスです。
            </p>
          </motion.div>

          {/* Content Sections */}
          <div className="space-y-12">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="bg-white rounded-[24px] p-10 shadow-[0_16px_48px_rgba(255,158,122,0.12)]"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF9E7A] to-[#FFBC9E] flex items-center justify-center">
                  <i className="ri-lightbulb-line text-[24px] text-white"></i>
                </div>
                <h2 className="text-[28px] font-bold text-[#19425B]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  私たちのビジョン
                </h2>
              </div>
              <p className="text-[16px] leading-[1.9] text-[rgba(25,66,91,0.8)]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                琉球の文化と伝統を大切にしながら、現代に生きる琉球人オーナーたちが営むお店を、より多くの人に知ってもらいたい。そんな想いから、このプロジェクトは始まりました。地域に根ざした小さなお店一つひとつが、琉球の豊かな文化を次世代へとつなぐ大切な場所だと私たちは信じています。
              </p>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="bg-white rounded-[24px] p-10 shadow-[0_16px_48px_rgba(255,158,122,0.12)]"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF9E7A] to-[#FFBC9E] flex items-center justify-center">
                  <i className="ri-compass-3-line text-[24px] text-white"></i>
                </div>
                <h2 className="text-[28px] font-bold text-[#19425B]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  ミッション
                </h2>
              </div>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF9E7A] to-[#FFBC9E] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[16px] leading-[1.9] text-[rgba(25,66,91,0.8)]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    <strong className="text-[#19425B]">発見の場を提供：</strong>琉球人オーナーのお店を簡単に見つけられるマップを提供し、新しい出会いを創出します。
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF9E7A] to-[#FFBC9E] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[16px] leading-[1.9] text-[rgba(25,66,91,0.8)]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    <strong className="text-[#19425B]">コミュニティの強化：</strong>お店とお客様、そして地域住民をつなぎ、支え合うコミュニティを育てます。
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF9E7A] to-[#FFBC9E] mt-2.5 flex-shrink-0"></div>
                  <p className="text-[16px] leading-[1.9] text-[rgba(25,66,91,0.8)]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    <strong className="text-[#19425B]">文化の継承：</strong>琉球の伝統や文化を大切にするお店を応援し、次世代へと受け継ぎます。
                  </p>
                </div>
              </div>
            </motion.div>

            {/* How It Works */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="bg-white rounded-[24px] p-10 shadow-[0_16px_48px_rgba(255,158,122,0.12)]"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF9E7A] to-[#FFBC9E] flex items-center justify-center">
                  <i className="ri-map-2-line text-[24px] text-white"></i>
                </div>
                <h2 className="text-[28px] font-bold text-[#19425B]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  使い方
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 rounded-[16px] bg-gradient-to-br from-[#FFF8F3] to-[#FFF5ED]">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#FF9E7A] to-[#FFBC9E] flex items-center justify-center">
                    <span className="text-[28px] font-bold text-white" style={{ fontFamily: "'Inter', sans-serif" }}>1</span>
                  </div>
                  <h3 className="text-[18px] font-bold text-[#19425B] mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    マップで探す
                  </h3>
                  <p className="text-[14px] text-[rgba(25,66,91,0.7)] leading-[1.7]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    地図上でお店を探したり、カテゴリーで絞り込んだり
                  </p>
                </div>
                <div className="text-center p-6 rounded-[16px] bg-gradient-to-br from-[#FFF8F3] to-[#FFF5ED]">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#FF9E7A] to-[#FFBC9E] flex items-center justify-center">
                    <span className="text-[28px] font-bold text-white" style={{ fontFamily: "'Inter', sans-serif" }}>2</span>
                  </div>
                  <h3 className="text-[18px] font-bold text-[#19425B] mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    詳細を確認
                  </h3>
                  <p className="text-[14px] text-[rgba(25,66,91,0.7)] leading-[1.7]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    営業時間や場所、オーナーのストーリーをチェック
                  </p>
                </div>
                <div className="text-center p-6 rounded-[16px] bg-gradient-to-br from-[#FFF8F3] to-[#FFF5ED]">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#FF9E7A] to-[#FFBC9E] flex items-center justify-center">
                    <span className="text-[28px] font-bold text-white" style={{ fontFamily: "'Inter', sans-serif" }}>3</span>
                  </div>
                  <h3 className="text-[18px] font-bold text-[#19425B] mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    訪問して応援
                  </h3>
                  <p className="text-[14px] text-[rgba(25,66,91,0.7)] leading-[1.7]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    実際に訪れて、お店を応援しましょう
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Join Us */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="bg-gradient-to-br from-[#FF9E7A] to-[#FFBC9E] rounded-[24px] p-10 text-center shadow-[0_16px_48px_rgba(255,158,122,0.25)]"
            >
              <h2 className="text-[32px] font-bold text-white mb-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                お店の掲載をご希望の方へ
              </h2>
              <p className="text-[16px] text-[rgba(255,255,255,0.9)] leading-[1.8] mb-8 max-w-[600px] mx-auto" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                琉球人オーナーとして営業されているお店の掲載を無料で受け付けています。<br />
                お気軽にお問い合わせください。
              </p>
              <a
                href="mailto:info@ryukyu-map.com"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#FF9E7A] text-[16px] font-bold shadow-[0_8px_24px_rgba(0,0,0,0.15)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.2)] hover:-translate-y-1 transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <i className="ri-mail-line text-[22px]"></i>
                お問い合わせ
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
